import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Archive, Trash2, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function ReturnsInspectionPage() {
    const [pendingReturns, setPendingReturns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Estado para el modal de inspección
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [inspectionItems, setInspectionItems] = useState({}); // { productId: 'restock' | 'discard' }
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    useEffect(() => {
        fetchPendingReturns();
    }, []);

    const fetchPendingReturns = async () => {
        setIsLoading(true);
        try {
            // Asumimos un endpoint para listar devoluciones, filtrando por estado 'pending'
            // Si no existe, tendremos que crearlo o filtrar en cliente. 
            // POR AHORA: Usaremos un endpoint genérico /returns/ con filtro si es posible, 
            // o asumiremos que necesitamos crear GetAllReturns en backend.
            // Dado que no creamos ese endpoint en el plan explícitamente, vamos a intentar usar
            // una ruta que liste devoluciones. Si falla, añadiré el endpoint al backend.

            // Revisando 'backend/app/api/returns.py' en mi mente... solo vi POST. 
            // NECESITAMOS UN GET. Agregaré un GET rápido al final de esta implementación si no funciona.
            const response = await axios.get(`${API}/returns/?status=pending`);
            setPendingReturns(response.data || []);
        } catch (error) {
            console.error('Error fetching returns:', error);
            // Fallback silencioso por si el endpoint aun no existe
            toast.error("No se pudieron cargar las devoluciones pendientes.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInspect = (returnDoc) => {
        setSelectedReturn(returnDoc);
        // Inicializar decisiones por defecto a 'restock' (o dejarlas vacías para obligar a elegir)
        const initialDecisions = {};
        returnDoc.items.forEach(item => {
            initialDecisions[item.product_id] = 'restock';
        });
        setInspectionItems(initialDecisions);
        setIsDialogOpen(true);
    };

    const handleDecisionChange = (productId, decision) => {
        setInspectionItems(prev => ({
            ...prev,
            [productId]: decision
        }));
    };

    const handleSubmitInspection = async () => {
        setIsProcessing(true);
        try {
            const itemsPayload = Object.entries(inspectionItems).map(([productId, action]) => ({
                product_id: productId,
                action: action
            }));

            await axios.post(`${API}/returns/${selectedReturn.id}/process`, {
                items: itemsPayload
            });

            toast.success("Devolución procesada y stock actualizado correctamente.");
            setIsDialogOpen(false);
            fetchPendingReturns(); // Recargar lista
        } catch (error) {
            console.error(error);
            toast.error("Error al procesar la inspección.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) return <div className="p-8 text-center">Cargando devoluciones...</div>;

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold flex items-center gap-2">
                <ClipboardList className="size-8" /> Inspección de Devoluciones
            </h1>

            <Card>
                <CardHeader>
                    <CardTitle>Pendientes de Revisión</CardTitle>
                    <CardDescription>
                        Revise los productos devueltos y decida si reingresan al stock o se desechan.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {pendingReturns.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No hay devoluciones pendientes de inspección.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>ID Venta Original</TableHead>
                                    <TableHead>Motivo</TableHead>
                                    <TableHead className="text-right">Items</TableHead>
                                    <TableHead className="text-right">Acción</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendingReturns.map((ret) => (
                                    <TableRow key={ret.id}>
                                        <TableCell>{new Date(ret.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell className="font-mono text-xs">{ret.sale_id.substring(0, 8)}...</TableCell>
                                        <TableCell className="max-w-md truncate" title={ret.reason}>{ret.reason}</TableCell>
                                        <TableCell className="text-right">{ret.items.length}</TableCell>
                                        <TableCell className="text-right">
                                            <Button size="sm" onClick={() => handleInspect(ret)}>
                                                <CheckCircle className="mr-2 size-4" /> Inspeccionar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Modal de Inspección */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Procesar Devolución</DialogTitle>
                        <DialogDescription>
                            Para cada producto, determine su destino final.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedReturn && (
                        <div className="space-y-4">
                            <div className="bg-muted p-3 rounded-md text-sm">
                                <p><strong>Motivo del Cliente:</strong> {selectedReturn.reason}</p>
                            </div>

                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Producto</TableHead>
                                        <TableHead className="text-right">Cant.</TableHead>
                                        <TableHead>Decisión</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedReturn.items.map((item) => (
                                        <TableRow key={item.product_id}>
                                            <TableCell>{item.product_name}</TableCell>
                                            <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                                            <TableCell>
                                                <RadioGroup
                                                    value={inspectionItems[item.product_id]}
                                                    onValueChange={(val) => handleDecisionChange(item.product_id, val)}
                                                    className="flex items-center gap-4"
                                                >
                                                    <div className="flex items-center space-x-2 border p-2 rounded-md hover:bg-green-50 data-[state=checked]:border-green-500 cursor-pointer">
                                                        <RadioGroupItem value="restock" id={`r-${item.product_id}`} />
                                                        <Label htmlFor={`r-${item.product_id}`} className="flex items-center cursor-pointer">
                                                            <Archive className="size-4 mr-1 text-green-600" /> Reingresar
                                                        </Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2 border p-2 rounded-md hover:bg-red-50 cursor-pointer">
                                                        <RadioGroupItem value="discard" id={`d-${item.product_id}`} />
                                                        <Label htmlFor={`d-${item.product_id}`} className="flex items-center cursor-pointer">
                                                            <Trash2 className="size-4 mr-1 text-red-600" /> Desechar
                                                        </Label>
                                                    </div>
                                                </RadioGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                                <Button onClick={handleSubmitInspection} disabled={isProcessing}>
                                    {isProcessing ? "Procesando..." : "Confirmar y Finalizar"}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
